import React, { useState } from "react";
import Logo from "../Auth/LOGO5.png";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ShieldIcon from "@mui/icons-material/Shield";
import { useNavigate } from "react-router";
import { userLogin } from "../Services/Services";
import {
  storeToken,
  storeResponse,
  removeToken,
} from "../Services/LocalStorageService";
import { usePermission } from "../context/PermissionContext"; // ← ADD

function LoginPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const { refreshPermissions } = usePermission(); // ← ADD

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please fill in both email and password");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await userLogin({ email, password });

      if (response?.token) {
        removeToken();
        storeToken(response.token);
        storeResponse(response.user);
        refreshPermissions(); // ← ADD: re-read localStorage into context
        navigate("/dashboard");
      } else {
        setError("Invalid credentials. Please try again");
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response.data?.detail || "Server error. Please try again."
        );
      } else if (err.request) {
        setError("Network error: No response from server");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    // ... rest of your JSX stays exactly the same
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        background: `
          linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%),
          url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80')
        `,
        backgroundSize: "cover, cover",
        backgroundPosition: "center, center",
        backgroundBlendMode: "overlay",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(245, 158, 11, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)",
          top: "-200px",
          right: "-200px",
          animation: "float 8s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)",
          bottom: "-100px",
          left: "-100px",
          animation: "float 10s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />

      <Zoom in={mounted} timeout={500}>
        <Box
          sx={{
            width: isMobile ? "100%" : isTablet ? "450px" : "440px",
            borderRadius: "24px",
            p: isMobile ? 3 : 5,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            background: `
              linear-gradient(145deg, 
                rgba(255, 255, 255, 0.1) 0%, 
                rgba(255, 255, 255, 0.05) 100%
              )
            `,
            boxShadow: `
              0 25px 50px -12px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            `,
            border: "1px solid rgba(255, 255, 255, 0.15)",
            textAlign: "center",
            color: "#fff",
            zIndex: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
            },
          }}
        >
          {/* Logo Section */}
          <Fade in={mounted} timeout={800}>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "20px",
                  background:
                    "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 2,
                  boxShadow: "0 10px 40px rgba(245, 158, 11, 0.4)",
                  position: "relative",
                  overflow: "hidden",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    animation: "shimmer 2s infinite",
                  },
                }}
              >
                <LocalShippingIcon sx={{ fontSize: 40, color: "#0f172a" }} />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "3px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                }}
              >
                Logistics & Transport Management
              </Typography>
            </Box>
          </Fade>

          {/* Error Message */}
          <Fade in={!!error}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "12px",
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                display: error ? "block" : "none",
              }}
            >
              <Typography
                sx={{
                  color: "#fca5a5",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            </Box>
          </Fade>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2.5 }}>
              <TextField
                label="Email Address"
                type="email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  sx: {
                    color: "#fff",
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      borderRadius: "12px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(245, 158, 11, 0.5)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#fbbf24" },
                  },
                }}
                InputLabelProps={{
                  sx: { color: "rgba(255, 255, 255, 0.6)" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  sx: {
                    color: "#fff",
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      borderRadius: "12px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(245, 158, 11, 0.5)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#fbbf24" },
                  },
                }}
                InputLabelProps={{
                  sx: { color: "rgba(255, 255, 255, 0.6)" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              endIcon={!loading && <KeyboardArrowRightIcon />}
              sx={{
                py: 1.5,
                background:
                  "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                color: "#0f172a",
                fontWeight: 700,
                fontSize: "1rem",
                borderRadius: "12px",
                textTransform: "none",
                letterSpacing: "0.5px",
                transition:
                  "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                boxShadow: "0 4px 20px rgba(245, 158, 11, 0.4)",
                "&:hover": {
                  transform: "translateY(-2px) scale(1.02)",
                  boxShadow: "0 8px 30px rgba(245, 158, 11, 0.6)",
                  background:
                    "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)",
                },
                "&:active": {
                  transform: "translateY(0) scale(0.98)",
                },
                "&.Mui-disabled": {
                  background: "rgba(255, 255, 255, 0.2)",
                  color: "rgba(255, 255, 255, 0.5)",
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#0f172a" }} />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Security Badge */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.75rem",
            }}
          >
            <ShieldIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: "inherit" }}>
              Secure SSL Encrypted Connection
            </Typography>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              my: 3,
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
            }}
          />

          {/* Footer */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontWeight: 500,
              }}
            >
              Powered by{" "}
              <a
                href="https://www.yashvitech.com/"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#fbbf24",
                  textDecoration: "none",
                  fontWeight: 600,
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#f59e0b")}
                onMouseLeave={(e) => (e.target.style.color = "#fbbf24")}
              >
                YASHVITECH IT SOLUTIONS PVT LTD
              </a>
            </Typography>
          </Box>
        </Box>
      </Zoom>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </Box>
  );
}

export default LoginPage;